/**
 * Куда от знака встают превью макетов (docs/wayfinding/plan.md §9.1.4).
 *
 * У многостороннего носителя превью стоят НЕ рядом, а с тех сторон точки, которым они
 * отвечают: у двустороннего (прямоугольник 2:1) — сверху и снизу, у трёхстороннего —
 * по трём сторонам треугольника, у четырёхстороннего — по четырём сторонам квадрата.
 * Углы считаются от формы знака, а не задаются руками: знак и превью тогда не разъезжаются.
 *
 * Угол — градусы от «вверх» ПО ЧАСОВОЙ стрелке (экранные оси, y вниз): 0 — вверх,
 * 90 — вправо, 180 — вниз. `null` — превью стоит по центру точки, закрывая знак.
 */
import {
  WAYFINDING_SIGN_MAX_SIDES,
  type WayfindingSignType,
} from '../types/wayfinding.js';

/**
 * Куда смотрит превью при таком `icon-anchor`: якорь называет часть превью, которая стоит
 * В точке, значит само превью уходит в противоположную сторону.
 */
const ANCHOR_ANGLE: Readonly<Record<string, number | null>> = {
  center: null,
  bottom: 0,
  'bottom-left': 45,
  left: 90,
  'top-left': 135,
  top: 180,
  'top-right': 225,
  right: 270,
  'bottom-right': 315,
};

/** Направление превью по значению якоря; `undefined` — якорь не задан или незнаком. */
export function wayfindingAnchorDirectionAngle(anchor: unknown): number | null | undefined {
  if (typeof anchor !== 'string') {
    return undefined;
  }
  return anchor in ANCHOR_ANGLE ? ANCHOR_ANGLE[anchor] : undefined;
}

/**
 * Углы сторон по числу сторон: стороны делят круг поровну. У трёхстороннего отсчёт сдвинут
 * на −60°, чтобы стороны совпали с гранями треугольного знака (вверх-влево, вверх-вправо,
 * вниз); у остальных первая сторона смотрит вверх, как верхняя грань знака.
 */
export function wayfindingSignSideAngles(sides: number): number[] {
  const count = Math.max(1, Math.min(WAYFINDING_SIGN_MAX_SIDES, Math.floor(sides) || 1));
  const start = count === 3 ? -60 : 0;
  return Array.from({ length: count }, (_, index) => start + (index * 360) / count);
}

/** Какая точка превью стоит в точке знака — доли ширины и высоты картинки. */
const ANCHOR_FRACTION: Readonly<Record<string, readonly [number, number]>> = {
  center: [0.5, 0.5],
  top: [0.5, 0],
  bottom: [0.5, 1],
  left: [0, 0.5],
  right: [1, 0.5],
  'top-left': [0, 0],
  'top-right': [1, 0],
  'bottom-left': [0, 1],
  'bottom-right': [1, 1],
};

/**
 * Якорь по умолчанию: у одностороннего — `left` (превью справа от знака, снизу у него
 * подпись), у многостороннего — `bottom` (превью растут от точки наружу, первая сторона
 * вверх, как верхняя грань знака).
 */
export function wayfindingSignDefaultPreviewAnchor(sides: number | null | undefined): string {
  return (sides ?? 1) > 1 ? 'bottom' : 'left';
}

type PreviewDirectionType = Pick<
  WayfindingSignType,
  'sides' | 'previewAnchor' | 'previewAnchorByPage'
>;

/** Как стоит превью одной стороны: куда от точки, как повёрнуто и чем касается точки. */
export interface WayfindingSignPreviewPlacement {
  /** Куда от точки уходит превью, градусы от «вверх» по часовой; `null` — по центру точки. */
  angleDeg: number | null;
  /** Поворот самой картинки, градусы по часовой. */
  rotationDeg: number;
  /** Точка превью, которая стоит в точке знака: доли ширины и высоты. */
  anchor: readonly [number, number];
}

/**
 * Направления превью сторон носителя, по одному на сторону.
 *
 * Односторонний знак стоит там, куда показывает якорь типа. У многостороннего углы
 * берутся от формы, а якорь типа ПОВОРАЧИВАЕТ всю раскладку — первая сторона встаёт туда,
 * куда показывает якорь. Якорь «по центру» у многостороннего игнорируется: сложить все
 * превью в одну точку значит потерять и их, и знак.
 */
export function wayfindingSignPreviewDirections(
  type: PreviewDirectionType | null | undefined,
): Array<number | null> {
  const sides = Math.max(1, Math.min(WAYFINDING_SIGN_MAX_SIDES, Math.floor(type?.sides ?? 1) || 1));
  const anchorAngle = wayfindingAnchorDirectionAngle(type?.previewAnchor);
  if (sides === 1) {
    return [
      anchorAngle === undefined
        ? wayfindingAnchorDirectionAngle(wayfindingSignDefaultPreviewAnchor(1))!
        : anchorAngle,
    ];
  }
  const base = wayfindingSignSideAngles(sides);
  if (anchorAngle === undefined || anchorAngle === null) {
    return base;
  }
  const turn = anchorAngle - base[0]!;
  return base.map((angle) => normalizeAngle(angle + turn));
}

/**
 * Раскладка превью сторон: направление, поворот картинки и точка касания.
 *
 * Точки знака все стороны касаются ОДНОЙ И ТОЙ ЖЕ своей частью — той, что названа якорем
 * типа. Значит превью противоположной стороны разворачивается: у двусторонней стелы с
 * якорем «сверху» оба макета висят верхом у точки, и нижний виден перевёрнутым. Иначе одна
 * сторона стояла бы к точке верхом, другая низом (решение Паши, 12.09).
 *
 * @param pageBySide показанная страница каждой стороны: у страницы бывает свой якорь —
 *   тогда сторона встаёт по нему и не поворачивается (страницу человек выбирает сам).
 *
 * Якорь страницы у многостороннего действует, только пока сторона остаётся на СВОЁМ месте:
 * две стороны в одном направлении лежали бы одна на другой, и раскладка, раздвигая их, унесла
 * бы обе далеко от точки (у старого типа с якорем «Страница 2 — сверху» при якоре типа
 * «сверху» так вышло, когда разводка поставила сторону 2 на страницу 2). Уступает сторона
 * с якорем страницы — у стороны без него другого места нет; из двух с якорями — старшая по
 * номеру. Уступившая встаёт на место своей стороны. «По центру» у многостороннего не
 * действует и у страницы — по той же причине, что у типа.
 *
 * @param drawnSides есть ли у стороны превью; нет записи — есть. Совпадение считается только
 *   между нарисованными: пустое место стороны без макета не должно сгонять видимое превью
 *   с якоря его страницы.
 */
export function wayfindingSignPreviewPlacements(
  type: PreviewDirectionType | null | undefined,
  pageBySide: readonly number[] = [],
  drawnSides: readonly boolean[] = [],
): WayfindingSignPreviewPlacement[] {
  const anchorName = type?.previewAnchor ?? wayfindingSignDefaultPreviewAnchor(type?.sides);
  const anchorAngle = wayfindingAnchorDirectionAngle(anchorName) ?? null;
  const anchor = ANCHOR_FRACTION[anchorName] ?? ANCHOR_FRACTION.center!;
  const directions = wayfindingSignPreviewDirections(type);
  const bySide = directions.map((angleDeg): WayfindingSignPreviewPlacement => {
    if (angleDeg === null || anchorAngle === null) {
      return { angleDeg, rotationDeg: 0, anchor };
    }
    return { angleDeg, rotationDeg: normalizeAngle(angleDeg - anchorAngle), anchor };
  });
  const multiSide = directions.length > 1;
  const byPage = directions.map((_, index): WayfindingSignPreviewPlacement | null => {
    const pageAnchor = readPageAnchorName(type, pageBySide[index] ?? 1);
    if (!pageAnchor || (multiSide && pageAnchor === 'center')) {
      return null;
    }
    // У страницы свой якорь: превью встаёт по нему как одиночное — без поворота.
    return {
      angleDeg: wayfindingAnchorDirectionAngle(pageAnchor) ?? null,
      rotationDeg: 0,
      anchor: ANCHOR_FRACTION[pageAnchor] ?? ANCHOR_FRACTION.center!,
    };
  });
  // Каждый шаг снимает один якорь страницы, а места сторон без якорей не совпадают никогда —
  // цикл конечен.
  const drawn = directions.map((_, index) => drawnSides[index] ?? true);
  for (let yielding = collidingPageAnchorSide(bySide, byPage, drawn); yielding !== -1; ) {
    byPage[yielding] = null;
    yielding = collidingPageAnchorSide(bySide, byPage, drawn);
  }
  return bySide.map((placement, index) => byPage[index] ?? placement);
}

/**
 * Нарисованная сторона, чей якорь страницы ставит её в одно направление с другой нарисованной;
 * `-1` — таких нет. Уступает сторона с якорем страницы, из двух таких — старшая по номеру.
 */
function collidingPageAnchorSide(
  bySide: readonly WayfindingSignPreviewPlacement[],
  byPage: readonly (WayfindingSignPreviewPlacement | null)[],
  drawn: readonly boolean[],
): number {
  const direction = (index: number): number | null => {
    const angle = (byPage[index] ?? bySide[index]!).angleDeg;
    return angle === null ? null : normalizeAngle(angle);
  };
  for (let later = 1; later < bySide.length; later += 1) {
    for (let earlier = 0; earlier < later; earlier += 1) {
      if (!drawn[earlier] || !drawn[later] || direction(earlier) !== direction(later)) {
        continue;
      }
      if (byPage[later]) {
        return later;
      }
      if (byPage[earlier]) {
        return earlier;
      }
    }
  }
  return -1;
}

/** Имя якоря страницы, если он задан и знаком. */
function readPageAnchorName(
  type: Pick<WayfindingSignType, 'previewAnchorByPage'> | null | undefined,
  page: number,
): string | null {
  const value = type?.previewAnchorByPage?.[String(page)];
  return typeof value === 'string' && value in ANCHOR_FRACTION ? value : null;
}

/** Угол в [0, 360) — чтобы одинаковые раскладки давали одинаковые имена иконок. */
function normalizeAngle(angle: number): number {
  const wrapped = angle % 360;
  const positive = wrapped < 0 ? wrapped + 360 : wrapped;
  // −0 и 360 — это 0: имя иконки собирается из этих чисел.
  return positive === 0 ? 0 : Math.round(positive * 100) / 100;
}
